<template>
  <div class="heatmap_svg" :style="heatmapZoom">
  </div>

</template>

<script>
import h337 from 'heatmap.js'

export default {
  name: 'HeatmapSvg',
  props: [
    'svgData',
    'device',
    'eventType',
    'scale',
    'containerScale',
    'captureHeight',
    'heatmapConcentration',
    'changeConcentration'
  ],
  data () {
    return {
      isLoadedOnce: false,
      heatmapInstance: null,
      heatmapData: null,
      currentConcentration: this.heatmapConcentration || 50
    }
  },
  computed: {
    isEventTypeClick () {
      return this.eventType === 'click'
    },
    heatmapZoom () {
      return {
        '--heatmap-ratio': this.getHeatmapRatio(),
        '--heatmap-percent': 100 / this.getHeatmapRatio() + '%'
      }
    }
  },
  watch: {
    scale (value) {
      this.clearHeatmap()
      this.loadHeatmap()
    },
    heatmapConcentration (value) {
      if (value !== undefined && value !== null) {
        this.currentConcentration = value
      }

      // 농도 값에 따라 히트맵 opacity 조정
      if (this.heatmapInstance && value) {
        // 농도 값은 0~100 사이의 값으로 가정
        const maxOpacity = this.calculateOpacity(value, true)
        const minOpacity = this.calculateOpacity(value, false)

        // 설정 업데이트
        this.updateHeatmapConfig({
          maxOpacity: maxOpacity,
          minOpacity: minOpacity,
          blur: this.isEventTypeClick ? 0.6 : 1
        })

        // 히트맵 다시 그리기 (데이터가 있는 경우)
        if (this.heatmapInstance && this.heatmapInstance.getData()) {
          this.heatmapInstance.repaint()
        }
      }
    }
  },
  mounted () {
    if (!this.isLoadedOnce) {
      this.loadHeatmap()
    }
  },
  destroyed () {
    this.clearHeatmap()
  },
  methods: {
    clearHeatmap () {
      const canvasEls = this.$el.getElementsByTagName('canvas')

      if (canvasEls.length) {
        canvasEls[0].parentNode.removeChild(canvasEls[0])
      }
    },
    getHeatmapRatio () {
      let ratio = 2
      let canvasRatio = 1600 / 700
      if (this.device === 'tablet') {
        ratio = 3
        canvasRatio = 1280 / 700
      } else if (this.device === 'phone') {
        ratio = 3
        canvasRatio = 375 / 438
      }
      if (this.captureHeight / canvasRatio > 16000) ratio = 5
      return ratio
    },
    calculateOpacity (concentrationValue, isMax) {
      const baseMaxOpacity = this.isEventTypeClick ? 0.4 : 0.2
      const targetMinOpacity = 0.15

      if (isMax) {
        if (concentrationValue >= 50) {
          const adjustment = ((concentrationValue - 50) / 50) * 1.5
          return Math.min(1, baseMaxOpacity + adjustment)
        } else {
          return targetMinOpacity + (baseMaxOpacity - targetMinOpacity) * (concentrationValue / 50)
        }
      } else {
        const baseMinOpacity = this.isEventTypeClick ? 0.03 : 0.06
        if (concentrationValue >= 50) {
          const adjustment = ((concentrationValue - 50) / 50) * 0.15
          return Math.min(0.3, baseMinOpacity + adjustment)
        } else {
          return baseMinOpacity * (concentrationValue / 50)
        }
      }
    },
    // 히트맵 설정 업데이트
    updateHeatmapConfig (config) {
      if (!this.heatmapInstance) return

      // 히트맵 설정 업데이트
      this.heatmapInstance.configure(config)
    },
    loadHeatmap () {
      this.isLoadedOnce = true
      const radius = (this.device === 'desktop' ? 10 : 18) * this.containerScale / this.getHeatmapRatio()
      const maxOpacity = this.isEventTypeClick ? 0.4 : 0.2
      const minOpacity = this.isEventTypeClick ? 0.03 : 0.06
      const blur = this.isEventTypeClick ? 0.6 : 1
      let gradient

      if (this.isEventTypeClick) {
        gradient = {
          '0.2': 'blue',
          '0.35': 'cyan',
          '0.4': 'lime',
          '0.55': 'yellow',
          '0.63': 'orange',
          '0.9': 'red',
          '1': 'white'
        }
      } else {
        gradient = {
          '0.1': 'blue',
          '0.25': 'cyan',
          '0.3': 'lime',
          '0.55': 'yellow',
          '0.63': 'orange',
          '0.9': 'red',
          '1': 'white'
        }
      }

      this.heatmapInstance = h337.create({
        radius,
        maxOpacity,
        minOpacity,
        blur,
        gradient,
        container: this.$el
      })

      const svgData = this.normalizeRawData(this.svgData)
      const itemValues = svgData.map(h => h.value)
      itemValues.sort((a, b) => b - a)
      const maxValue = itemValues[Math.round(itemValues.length * 0.01)]

      this.heatmapData = {
        max: maxValue,
        data: svgData
      }

      this.heatmapInstance.setData(this.heatmapData)

      const initialConcentration = this.heatmapConcentration !== undefined ? this.heatmapConcentration : 50
      this.currentConcentration = initialConcentration
      if (initialConcentration !== 50 && initialConcentration) {
        const maxOpacity = this.calculateOpacity(initialConcentration, true)
        const minOpacity = this.calculateOpacity(initialConcentration, false)

        this.updateHeatmapConfig({
          maxOpacity,
          minOpacity,
          blur
        })
      }
    },
    normalizeRawData (data) {
      const rawData = []

      for (const xKey of Object.keys(data)) {
        for (const yKey of Object.keys(data[xKey])) {
          const value = data[xKey][yKey]
          const x = Math.round(xKey * this.scale) / this.getHeatmapRatio()
          const y = Math.round(yKey * this.scale) / this.getHeatmapRatio()

          if (x && y) {
            rawData.push({
              x,
              y,
              value
            })
          }
        }
      }
      return rawData
    }
  }
}
</script>

<style lang="scss">
.heatmap_svg {
  height: var(--heatmap-percent);
  width: var(--heatmap-percent);
  max-height: 16000px;
  transform: scale(var(--heatmap-ratio));
  transform-origin: 0 0;
  position: relative;
}
</style>
